/**
 * PathFinder — the documented chain between any two entities.
 * Each hop is a verified co-appearance; the evidence (shared dossiers)
 * renders inline at every step. No path = honest "no documented chain".
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, GitBranch, FileText, ArrowDown } from "lucide-react";
import { fetchPath, searchEntities, KIND_META, groupOf, pathForEntity, portraitSrc } from "./explorerApi";

const EntityPicker = ({ label, value, onPick, testid }) => {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);

  const search = async (text) => {
    setQ(text);
    if (text.trim().length < 2) { setHits([]); return; }
    try { setHits(await searchEntities(text.trim())); setOpen(true); }
    catch { setHits([]); }
  };

  return (
    <div className="relative flex-1 min-w-[220px]">
      <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065] mb-1">{label}</div>
      <input
        value={value ? value.name : q}
        onChange={(e) => { onPick(null); search(e.target.value); }}
        onFocus={() => hits.length && setOpen(true)}
        placeholder="Search the archive…"
        data-testid={testid}
        className="w-full bg-[#0d0c0a] border px-3 py-2 text-[13px] text-[#E4DFD3] font-heading outline-none focus:border-[#D92D20] transition-colors"
        style={{ borderColor: value ? "#3FB950" : "#3A3630" }}
      />
      {open && hits.length > 0 && !value && (
        <div className="absolute z-30 mt-1 w-full border max-h-56 overflow-y-auto"
             style={{ borderColor: "#3A3630", background: "#141210" }}>
          {hits.map((h) => (
            <button key={h.slug} type="button"
                    onClick={() => { onPick(h); setOpen(false); setQ(""); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#1d1a16] transition-colors flex items-center justify-between gap-2"
                    data-testid={`picker-hit-${h.slug}`}>
              <span className="text-[12px] text-[#E4DFD3] truncate">{h.name}</span>
              <span className="font-mono text-[8px] uppercase tracking-[0.15em] shrink-0"
                    style={{ color: KIND_META[groupOf(h)]?.color }}>
                {KIND_META[groupOf(h)]?.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const PathFinder = ({ initialFrom = null, initialTo = null }) => {
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!initialFrom || !initialTo) return;
    let dead = false;
    setBusy(true);
    fetchPath(initialFrom, initialTo)
      .then((r) => {
        if (dead) return;
        setResult(r);
        if (r.found) {
          setA(r.nodes[0]);
          setB(r.nodes[r.nodes.length - 1]);
        }
      })
      .catch(() => { if (!dead) setResult({ found: false, message: "Trace failed." }); })
      .finally(() => { if (!dead) setBusy(false); });
    return () => { dead = true; };
  }, [initialFrom, initialTo]);

  const trace = async () => {
    if (!a || !b) return;
    setBusy(true);
    setResult(null);
    try { setResult(await fetchPath(a.slug, b.slug)); }
    catch (e) { setResult({ found: false, message: e?.response?.data?.detail || "Trace failed." }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="path-finder">
      <div className="flex flex-wrap items-end gap-3">
        <EntityPicker label="From" value={a} onPick={setA} testid="path-from-input" />
        <EntityPicker label="To" value={b} onPick={setB} testid="path-to-input" />
        <button type="button" disabled={!a || !b || busy} onClick={trace}
                data-testid="trace-path-btn"
                className="inline-flex items-center gap-2 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20] border-[#D92D20] hover:bg-[#D92D20] hover:text-[#0a0907] transition-colors disabled:opacity-30">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <GitBranch size={11} />}
          Trace the chain
        </button>
      </div>

      {result && !result.found && (
        <div className="mt-6 border border-dashed p-5 font-mono text-[11px] text-[#A68F5E]"
             style={{ borderColor: "#3A3630" }} data-testid="path-not-found">
          {result.message || "No documented chain connects these entities."}
          <div className="text-[9px] text-[#5A554C] mt-1.5 normal-case tracking-normal">
            The Archive never invents connections — a chain appears here only when every
            step is documented in a published dossier.
          </div>
        </div>
      )}

      {result?.found && (
        <div className="mt-6" data-testid="path-result">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#3FB950]">
            documented chain found · {result.degrees} degree{result.degrees === 1 ? "" : "s"} of separation
          </div>
          <div className="mt-4 space-y-0">
            {result.nodes.map((n, i) => (
              <React.Fragment key={n.slug}>
                <Link to={pathForEntity(n)}
                      className="flex items-center gap-3 border p-3 hover:border-[#D92D20] transition-colors max-w-xl"
                      style={{ borderColor: "#3A3630", background: "#12110e" }}
                      data-testid={`path-node-${n.slug}`}>
                  <span className="w-10 h-12 shrink-0 flex items-center justify-center overflow-hidden"
                        style={{ border: "1px solid #221F1C", background: "#0B0A0D" }}>
                    {n.portrait_url
                      ? <img src={portraitSrc(n.portrait_url)} alt={n.name}
                             className="max-w-full max-h-full object-contain" style={{ filter: "sepia(0.15)" }} />
                      : <span className="font-mono text-[9px] text-[#454038]">FILE</span>}
                  </span>
                  <span>
                    <span className="block text-[14px] text-[#E4DFD3] font-heading leading-tight">{n.name}</span>
                    <span className="block font-mono text-[8px] uppercase tracking-[0.18em] mt-0.5"
                          style={{ color: KIND_META[groupOf(n)]?.color }}>
                      {KIND_META[groupOf(n)]?.label}
                    </span>
                  </span>
                </Link>
                {i < result.hops.length && result.hops[i].shared_dossiers.length > 0 && (
                  <div className="ml-6 pl-4 py-2 border-l-2 border-[#D92D20]/60 max-w-xl"
                       data-testid={`path-hop-${i}`}>
                    <div className="flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-[0.25em]"
                         style={{ color: result.hops[i].strength === "corroborated" ? "#3FB950" : "#A68F5E" }}>
                      <ArrowDown size={9} /> {result.hops[i].strength} co-appearance
                    </div>
                    <div className="mt-1 space-y-1">
                      {result.hops[i].shared_dossiers.map((d) => (
                        <Link key={d.slug} to={`/topic/${d.slug}`}
                              className="flex items-center gap-1.5 text-[11px] text-[#A39E93] hover:text-[#D92D20] transition-colors">
                          <FileText size={10} className="text-[#A68F5E] shrink-0" />{d.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
